import { Grid, Grow, IconButton, Stack } from "@mui/material";
import { HorizontalOnlyContainer } from "../../../component/HorizontalOnlyContainer";
import { GameData, TeamTurns } from "../../../../domain/model/Game";
import { Answer, Question } from "../../../../domain/model/Question";
import {
  PauseRounded,
  PlayArrowRounded,
  RestoreRounded,
} from "@mui/icons-material";
import {
  COLOR_BLACK,
  COLOR_MAIN,
  COLOR_VERY_LIGHT_GRAY,
  COLOR_WHITE,
} from "../../../../data/colors/Colors";
import { CustomTextView } from "../../../component/CustomTextView";
import { useEffect, useState } from "react";
import { QuestionViewModel } from "./QuestionViewModel";
import { CustomCardView } from "../../../component/CustomCardView";
import { formattedTime, getDifficultyInfo } from "../../../../core/utils/Utils";
import { CustomButtonView } from "../../../component/CustomButtonView";
import useWindowDimensions from "../../../../core/utils/UseWindowDimensions";
import { QuestionMobileView } from "./QuestionMobileView";
import { useTranslation } from "react-i18next";
import { QuestionAnswerDetailsView } from "./QuestionAnswerDetailsView";
import { CustomImageView } from "../../../component/CustomImageView";

export function QuestionView({
  gameDataInput,
  questionInput,
  answerInput,
  onClosePopover,
}: {
  isEditing?: boolean;
  gameDataInput: GameData;
  onClosePopover: ({ who }: { who?: string }) => void;
  questionInput: Question | undefined;
  answerInput: Answer | undefined;
}) {
  const viewModel = QuestionViewModel();

  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() == "rtl";

  useEffect(() => {
    if (!questionInput || !answerInput) return;
    viewModel.init(gameDataInput, questionInput, answerInput);

    setChecked(true);
  }, []);

  const gameData = viewModel.gameData;
  const question = viewModel.question;
  const answer = viewModel.answer;

  const { height } = useWindowDimensions();
  const isMobile = height < 700;

  const [checked, setChecked] = useState(false);

  const countdownTimerVisible = !viewModel.bothTeamAnswered;

  return (
    <HorizontalOnlyContainer>
      {!gameData || !question || !answer ? (
        "ERROR"
      ) : (
        <Stack
          width={isMobile ? "95vw" : "98vw"}
          height={isMobile ? "100vh" : "96vh"}
          alignItems={"center"}
          justifyContent={"center"}
          p={isMobile ? 1 : 2}
        >
          {isMobile ? (
            <QuestionMobileView
              onClosePopover={onClosePopover}
              viewModel={viewModel}
            />
          ) : (
            <Stack width={"100%"} height={"100%"} gap={2}>
              <Stack height={"100%"} width={"100%"}>
                <Grid container height={"100%"} width={"100%"} spacing={0}>
                  <Grid size={3} sx={{ pl: isRtl ? 2 : 0, pr: isRtl ? 0 : 2 }}>
                    <Stack
                      width={"100%"}
                      height={"100%"}
                      alignItems={"center"}
                      gap={2}
                    >
                      <CustomCardView
                        width={"100%"}
                        height={"100%"}
                        children={
                          <Grow
                            in={checked}
                            {...(checked ? { timeout: 1000 } : {})}
                            mountOnEnter
                            unmountOnExit
                          >
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              height={"100%"}
                              width={"100%"}
                              justifyContent={"space-between"}
                              position={"relative"}
                              spacing={1}
                              sx={{ direction: "ltr" }}
                            >
                              <Stack
                                width={"100%"}
                                height={"100%"}
                                justifyContent={
                                  !countdownTimerVisible ? "center" : "start"
                                }
                                alignItems={
                                  !countdownTimerVisible ? "center" : "end"
                                }
                                gap={2}
                              >
                                <CustomTextView
                                  text={question.category?.title}
                                  fontWeight="bold"
                                  textAlign="center"
                                  variant={!countdownTimerVisible ? "h4" : "h5"}
                                />
                                <CustomTextView
                                  text={
                                    getDifficultyInfo(
                                      question.difficulty ?? "",
                                      t,
                                    ).text
                                  }
                                  fontWeight="bold"
                                  variant={!countdownTimerVisible ? "h4" : "h5"}
                                  textAlign="center"
                                  textColor={
                                    getDifficultyInfo(
                                      question.difficulty ?? "",
                                      t,
                                    ).color
                                  }
                                />
                              </Stack>

                              <Stack
                                position={"absolute"}
                                width={"100%"}
                                justifyContent={"center"}
                                height={"100%"}
                                zIndex={2}
                              >
                                {countdownTimerVisible && (
                                  <Stack width={"100%"} height={"40%"}>
                                    <CountdownTimer
                                      counter={viewModel.countdownTimerSeconds}
                                      setCounter={(c) =>
                                        viewModel.setCountdownTimerSeconds(c)
                                      }
                                      setPausedResumed={(is) => {
                                        viewModel.setTimerPausedOrResumed(is);
                                      }}
                                      hasFirstTeamAnswered={
                                        viewModel.hasFirstTeamAnswered
                                      }
                                    />
                                  </Stack>
                                )}
                              </Stack>
                            </Stack>
                          </Grow>
                        }
                      />
                      <CustomCardView
                        width={"100%"}
                        height={"100%"}
                        children={
                          <Grow
                            in={checked}
                            {...(checked ? { timeout: 1000 } : {})}
                            mountOnEnter
                            unmountOnExit
                          >
                            <Stack
                              width={"100%"}
                              height={"100%"}
                              justifyContent={"center"}
                              alignItems={"center"}
                              gap={4}
                            >
                              <Stack
                                direction={"row"}
                                alignItems={"center"}
                                gap={4}
                              >
                                <CustomTextView
                                  text={gameData.firstTeamName}
                                  variant="h4"
                                  fontWeight={
                                    gameData.currentTeamTurn == TeamTurns.teamA
                                      ? "bold"
                                      : "normal"
                                  }
                                  textColor={COLOR_BLACK}
                                />

                                <CustomTextView
                                  text={gameData?.firstTeamScore?.toString()}
                                  fontWeight={
                                    gameData.currentTeamTurn == TeamTurns.teamA
                                      ? "bold"
                                      : "normal"
                                  }
                                  variant="h3"
                                  textColor={COLOR_BLACK}
                                />
                              </Stack>
                            </Stack>
                          </Grow>
                        }
                      />
                    </Stack>
                  </Grid>
                  <Grid size={6}>
                    <Stack
                      width={"100%"}
                      height={"100%"}
                      alignItems={"center"}
                      gap={2}
                    >
                      <CustomCardView
                        width={"100%"}
                        height={"85%"}
                        background={COLOR_MAIN}
                        children={
                          <>
                            {viewModel.bothTeamAnswered &&
                            viewModel.showWhoAnswered ? (
                              <Stack
                                width={"100%"}
                                height={"100%"}
                                justifyContent={"center"}
                                alignItems={"center"}
                                gap={1}
                              >
                                <Stack
                                  direction={"row"}
                                  width={"100%"}
                                  alignItems={"center"}
                                  justifyContent={"center"}
                                  gap={1}
                                >
                                  <Stack width={"100%"}>
                                    <CustomButtonView
                                      onClick={() => {
                                        onClosePopover({
                                          who: gameData.firstTeamName,
                                        });
                                      }}
                                      props={{ pt: 2, pb: 2 }}
                                      fontSize="h5"
                                      text={gameData.firstTeamName}
                                      backgroundColor={COLOR_WHITE}
                                      textColor={COLOR_MAIN}
                                    />
                                  </Stack>

                                  <Stack width={"100%"}>
                                    <CustomButtonView
                                      fontSize="h5"
                                      props={{ pt: 2, pb: 2 }}
                                      onClick={() => {
                                        onClosePopover({
                                          who: gameData.secondTeamName,
                                        });
                                      }}
                                      text={gameData.secondTeamName}
                                      backgroundColor={COLOR_WHITE}
                                      textColor={COLOR_MAIN}
                                    />
                                  </Stack>
                                </Stack>

                                <Stack width={"100%"}>
                                  <CustomButtonView
                                    fontSize="h5"
                                    onClick={() => {
                                      onClosePopover({});
                                    }}
                                    props={{ pt: 2, pb: 2 }}
                                    text={t("no_one_answered")}
                                    backgroundColor={COLOR_WHITE}
                                    textColor={COLOR_MAIN}
                                  />
                                </Stack>
                              </Stack>
                            ) : (
                              <QuestionAnswerDetailsView
                                question={question}
                                answer={answer}
                                showAnswer={viewModel.showAnswer}
                                isMobile={false}
                              />
                            )}
                          </>
                        }
                      />

                      <CustomCardView
                        width={"100%"}
                        height={"15%"}
                        background={COLOR_MAIN}
                        children={
                          <Stack
                            width={"100%"}
                            height={"100%"}
                            alignItems={"center"}
                            justifyContent={"center"}
                            direction={"row"}
                            gap={2}
                          >
                            {!viewModel.bothTeamAnswered ? (
                              <CustomButtonView
                                onClick={viewModel.onAnswerButtonClicked}
                                text={`${
                                  gameData.currentTeamTurn == TeamTurns.teamA
                                    ? !viewModel.hasFirstTeamAnswered
                                      ? gameData.firstTeamName
                                      : gameData.secondTeamName
                                    : !viewModel.hasFirstTeamAnswered
                                      ? gameData.secondTeamName
                                      : gameData.firstTeamName
                                } ${t("answered")}`}
                                backgroundColor={COLOR_VERY_LIGHT_GRAY}
                                textColor={COLOR_BLACK}
                                props={{
                                  pl: 14,
                                  pr: 14,
                                  direction: "ltr",
                                }}
                                elevation={12}
                                fontSize="h5"
                              />
                            ) : (
                              <>
                                {
                                  <CustomButtonView
                                    onClick={() => {
                                      viewModel.setShowWhoAnswered(true);
                                    }}
                                    text={t("who_answered")}
                                    backgroundColor={COLOR_VERY_LIGHT_GRAY}
                                    textColor={COLOR_BLACK}
                                    props={{ pl: 8, pr: 8 }}
                                    elevation={12}
                                    fontSize="h6"
                                  />
                                }
                              </>
                            )}

                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              gap={2}
                            >
                              {viewModel.bothTeamAnswered && (
                                <CustomButtonView
                                  onClick={() => {
                                    viewModel.setShowAnswer(
                                      !viewModel.showAnswer,
                                    );

                                    viewModel.setShowWhoAnswered(false);
                                  }}
                                  text={
                                    viewModel.showAnswer
                                      ? t("see_question")
                                      : t("see_answer")
                                  }
                                  backgroundColor={COLOR_VERY_LIGHT_GRAY}
                                  textColor={COLOR_BLACK}
                                  props={{ pl: 8, pr: 8 }}
                                  elevation={12}
                                  fontSize="h6"
                                />
                              )}
                            </Stack>
                          </Stack>
                        }
                      />
                    </Stack>
                  </Grid>
                  <Grid size={3} sx={{ pl: isRtl ? 0 : 2, pr: isRtl ? 2 : 0 }}>
                    <Stack
                      width={"100%"}
                      height={"100%"}
                      alignItems={"center"}
                      gap={2}
                    >
                      <CustomCardView
                        width={"100%"}
                        height={"100%"}
                        children={
                          <Grow
                            in={checked}
                            {...(checked ? { timeout: 1000 } : {})}
                            mountOnEnter
                            unmountOnExit
                          >
                            <Stack
                              width={"100%"}
                              height={"100%"}
                              justifyContent={"center"}
                              alignItems={"center"}
                              gap={4}
                            >
                              <Stack
                                direction={"row"}
                                alignItems={"center"}
                                gap={4}
                              >
                                <CustomTextView
                                  text={gameData?.secondTeamScore?.toString()}
                                  fontWeight={
                                    gameData.currentTeamTurn == TeamTurns.teamB
                                      ? "bold"
                                      : "normal"
                                  }
                                  textColor={COLOR_BLACK}
                                  variant="h3"
                                />
                                <CustomTextView
                                  text={gameData.secondTeamName}
                                  fontWeight={
                                    gameData.currentTeamTurn == TeamTurns.teamB
                                      ? "bold"
                                      : "normal"
                                  }
                                  textColor={COLOR_BLACK}
                                  variant="h4"
                                />
                              </Stack>
                            </Stack>
                          </Grow>
                        }
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </Stack>
            </Stack>
          )}
        </Stack>
      )}
    </HorizontalOnlyContainer>
  );
}

export function CountdownTimer({
  counter,
  setCounter,
  setPausedResumed,
  hasFirstTeamAnswered,
  isMobile = false,
}: {
  counter: number;
  setCounter: (count: number) => void;
  setPausedResumed: (paused: boolean) => void;
  hasFirstTeamAnswered: boolean;
  isMobile?: boolean;
}) {
  const [initialCounter, setInitialCounter] = useState(counter);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (counter > initialCounter) {
      setInitialCounter(counter);
    }
  }, [counter]);

  useEffect(() => {
    setPausedResumed(paused);
  }, [paused]);

  useEffect(() => {
    if (hasFirstTeamAnswered) {
      setInitialCounter(counter);
    }
  }, [hasFirstTeamAnswered]);

  useEffect(() => {
    const timer =
      counter > 0 &&
      setInterval(() => {
        if (paused) return;
        setCounter(counter - 1);
      }, 1000);
    if (timer) return () => clearInterval(timer);
  }, [counter, paused]);

  const handlePauseResumeCountdown = () => {
    setPaused(!paused);
  };

  const handleResetCountdown = () => {
    setCounter(initialCounter);
    setPaused(false);
  };

  return (
    <Stack
      gap={2}
      width={"100%"}
      height={"100%"}
      alignItems={"center"}
      justifyContent={"space-between"}
      direction={isMobile ? "row" : "column"}
      sx={{ direction: "ltr" }}
    >
      <Stack
        gap={1}
        width={isMobile ? "30%" : "100%"}
        height={"100%"}
        alignItems={isMobile ? "start" : "center"}
        direction={isMobile ? "column" : "row"}
        justifyContent={"center"}
      >
        <IconButton
          onClick={handleResetCountdown}
          sx={{ width: "4vw", height: "4vw" }}
        >
          <RestoreRounded
            sx={{
              color: isMobile ? COLOR_WHITE : COLOR_BLACK,
              width: "3vw",
              height: "3vw",
            }}
            fontSize={isMobile ? "small" : "large"}
          />
        </IconButton>

        <IconButton
          onClick={handlePauseResumeCountdown}
          sx={{ width: "4vw", height: "4vw" }}
        >
          {paused ? (
            <PlayArrowRounded
              sx={{
                color: isMobile ? COLOR_WHITE : COLOR_BLACK,
                width: "3vw",
                height: "3vw",
              }}
              fontSize={isMobile ? "small" : "large"}
            />
          ) : (
            <PauseRounded
              sx={{
                color: isMobile ? COLOR_WHITE : COLOR_BLACK,
                width: "3vw",
                height: "3vw",
              }}
              fontSize={isMobile ? "small" : "large"}
            />
          )}
        </IconButton>
      </Stack>

      <Stack
        width={isMobile ? "70%" : "100%"}
        height={"100%"}
        alignItems={isMobile ? "end" : "center"}
        justifyContent={"center"}
      >
        <CustomTextView
          text={counter > 0 ? formattedTime(counter) : "-"}
          variant={isMobile ? "h5" : "h3"}
          fontWeight="bold"
          textColor={isMobile ? COLOR_WHITE : COLOR_BLACK}
        />
      </Stack>
    </Stack>
  );
}
